import { type MouseEventHandler } from 'react';
import clsx from 'clsx';

// ** MUI Components
import { styled } from '@mui/material/styles';
import ToggleButtonGroup, { type ToggleButtonGroupProps } from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';

// ** Components
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';

// ** Hooks
import { useCurrentUser } from '@erikdakoda/ui/hooks/useCurrentUser';
import { useUpdateUserFields } from '@erikdakoda/ui/hooks/useUpdateUserFields';
import type { UserRole } from '@erikdakoda/database/enums';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  '& .MuiButtonBase-root.MuiToggleButton-root': {
    padding: '3px 8px',
    '& .MuiTypography-root': {
      lineHeight: 0.8,
      fontSize: '13px',
    },
    '&.Mui-disabled .MuiTypography-root': {
      color: theme.palette.text.disabled,
    },
  },
}));

export default function DisableAdminToggler(
  props: Omit<
    Partial<ToggleButtonGroupProps>,
    'value' | 'exclusive' | 'onChange' | 'onClick' | 'children' | 'disabled'
  >,
) {
  'use memo';

  const { user } = useCurrentUser();
  const { updateUserFields, isLoading } = useUpdateUserFields();

  const handleChange = async (event: React.MouseEvent<HTMLElement>, userRole: UserRole) => {
    const disableAdmin = userRole === 'USER';
    await updateUserFields(
      { disableAdmin },
      disableAdmin ? 'Admin features disabled' : 'Admin features enabled',
    );
  };

  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation();
  };

  if (user === null) {
    return null;
  }

  if (user.role === 'USER') {
    return (
      <Box className="disable-admin-toggler" sx={{ marginTop: '3px' }}>
        USER
      </Box>
    );
  }

  const toggleValue = user.disableAdmin ? 'USER' : user.role;

  return (
    <StyledToggleButtonGroup
      value={toggleValue}
      exclusive
      onChange={handleChange}
      onClick={handleClick}
      aria-label="Enable/disable admin features"
      size="small"
      disabled={isLoading}
      {...props}
      className={clsx('disable-admin-toggler', props.className)}
    >
      <Tooltip
        title={toggleValue === user.role ? 'Admin features enabled' : 'Enable admin features'}
      >
        <span>
          <ToggleButton value={user.role}>
            <Typography>{user.role}</Typography>
          </ToggleButton>
        </span>
      </Tooltip>
      <Tooltip
        title={toggleValue === 'USER' ? 'Admin features disabled' : 'Disable admin features'}
      >
        <span>
          <ToggleButton value="USER">
            <Typography>USER</Typography>
          </ToggleButton>
        </span>
      </Tooltip>
    </StyledToggleButtonGroup>
  );
}
